import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  LinearProgress,
  Avatar,
  Button,
  Fab,
} from '@mui/material';
import {
  Brain,
  Heart,
  Moon,
  Dumbbell,
  Activity,
  TrendingUp,
  Zap,
  Target,
  Plus,
  BarChart3,
  Settings,
  Calendar,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SurveyData, HealthMetrics } from '../types';
import QuickStats from './QuickStats';
import RecentActivity from './RecentActivity';
import HealthTrends from './HealthTrends';
import BiohackingInsights from './BiohackingInsights';

interface DashboardProps {
  surveyData: SurveyData[];
  healthMetrics: HealthMetrics;
  onSurveySubmit: (data: SurveyData) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ surveyData, healthMetrics, onSurveySubmit }) => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getOverallScore = () => {
    return healthMetrics.overallScore;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#00ff88';
    if (score >= 60) return '#ffd700';
    return '#ff6b6b';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  return (
    <>
      <AppBar position="static" sx={{ background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Brain size={32} color="#00ff88" />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
              PeakState
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`${getOverallScore()}% Overall`}
              sx={{
                background: `linear-gradient(45deg, ${getScoreColor(getOverallScore())}, ${getScoreColor(getOverallScore())}88)`,
                color: '#000',
                fontWeight: 600,
              }}
            />
            <IconButton onClick={() => navigate('/statistics')} sx={{ color: '#00ff88' }}>
              <BarChart3 size={20} />
            </IconButton>
            <IconButton onClick={() => navigate('/settings')} sx={{ color: '#00ff88' }}>
              <Settings size={20} />
            </IconButton>
            <Avatar sx={{ bgcolor: '#00ff88', color: '#000' }}>
              <Activity size={20} />
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h1" gutterBottom>
              Reach Your PeakState
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Real-time optimization dashboard for peak human performance
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<Zap />} label="Real-time Monitoring" color="primary" />
              <Chip icon={<Target />} label="Personalized Insights" color="secondary" />
              <Chip icon={<TrendingUp />} label="Performance Tracking" color="primary" />
            </Box>
          </Box>
        </motion.div>

        {/* Health Overview Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          {[
            { label: 'Stress Level', value: healthMetrics.stressLevel, icon: <Heart />, color: '#ff6b6b' },
            { label: 'Energy Level', value: healthMetrics.energyLevel, icon: <Zap />, color: '#ffd700' },
            { label: 'Sleep Quality', value: healthMetrics.sleepQuality, icon: <Moon />, color: '#4fc3f7' },
            { label: 'Physical Activity', value: healthMetrics.physicalActivity, icon: <Dumbbell />, color: '#00ff88' },
          ].map((metric, index) => (
            <Box key={metric.label} sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ color: metric.color }}>
                        {metric.icon}
                      </Box>
                      <Typography variant="h4" sx={{ color: metric.color, fontWeight: 700 }}>
                        {metric.value}%
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {metric.label}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={metric.value}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: metric.color,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </motion.div>
            </Box>
          ))}
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <HealthTrends surveyData={surveyData} />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <BiohackingInsights healthMetrics={healthMetrics} surveyData={surveyData} />
              </motion.div>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <QuickStats healthMetrics={healthMetrics} />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <RecentActivity surveyData={surveyData} />
              </motion.div>
            </Box>
          </Grid>
        </Grid>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#00ff88',
            color: '#000',
            '&:hover': {
              backgroundColor: '#00ff88',
              opacity: 0.9,
            },
          }}
          onClick={() => navigate('/survey')}
        >
          <Plus size={24} />
        </Fab>

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: {currentTime.toLocaleTimeString()} | 
            PeakState - Reaching Your Peak Human Performance
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Calendar />}
              onClick={() => navigate('/survey')}
              sx={{
                borderColor: '#00ff88',
                color: '#00ff88',
                '&:hover': {
                  borderColor: '#00ff88',
                  backgroundColor: 'rgba(0, 255, 136, 0.1)',
                },
              }}
            >
              Daily Survey
            </Button>
            <Button
              variant="outlined"
              startIcon={<BarChart3 />}
              onClick={() => navigate('/statistics')}
              sx={{
                borderColor: '#00ff88',
                color: '#00ff88',
                '&:hover': {
                  borderColor: '#00ff88',
                  backgroundColor: 'rgba(0, 255, 136, 0.1)',
                },
              }}
            >
              Statistics
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Dashboard;
